import { makeAutoObservable, runInAction } from "mobx";
import { OrderHistoryListItem } from "@domain/dtos/models/order.models";
import { OrderStatus } from "@domain/enums/order.enums";

/**
 * Store РґР»СЏ СѓРїСЂР°РІР»РµРЅРёСЏ Р·Р°РєР°Р·Р°РјРё
 * РСЃРїРѕР»СЊР·СѓРµС‚ MobX РґР»СЏ СЂРµР°РєС‚РёРІРЅРѕСЃС‚Рё Рё РїСЂРµРґРѕСЃС‚Р°РІР»СЏРµС‚ РёРЅС‚РµСЂС„РµР№СЃ РґР»СЏ React useSyncExternalStore
 */
class OrderStoreClass {
  private _orderHistory: OrderHistoryListItem[] = [];
  private _activeOrders: OrderHistoryListItem[] = []; // Отдельный список активных заказов (управляется через WebSocket)
  private _listeners = new Set<() => void>();

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * РџРѕРґРїРёСЃРєР° РЅР° РёР·РјРµРЅРµРЅРёСЏ СЃРѕСЃС‚РѕСЏРЅРёСЏ (РґР»СЏ useSyncExternalStore)
   */
  subscribe = (listener: () => void) => {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  };

  /**
   * РџРѕР»СѓС‡РµРЅРёРµ С‚РµРєСѓС‰РµРіРѕ СЃРѕСЃС‚РѕСЏРЅРёСЏ (РґР»СЏ useSyncExternalStore)
   */
  getState = () => {
    return {
      orderHistory: this.orderHistory,
      activeOrders: this.activeOrders
    };
  };

  /**
   * РЈРІРµРґРѕРјР»РµРЅРёРµ РІСЃРµС… РїРѕРґРїРёСЃС‡РёРєРѕРІ РѕР± РёР·РјРµРЅРµРЅРёРё СЃРѕСЃС‚РѕСЏРЅРёСЏ
   */
  private notify() {
    this._listeners.forEach(listener => listener());
  }

  /**
   * РџРѕР»РЅР°СЏ РёСЃС‚РѕСЂРёСЏ Р·Р°РєР°Р·РѕРІ
   */
  get orderHistory(): OrderHistoryListItem[] {
    return this._orderHistory;
  }

  /**
   * Активные заказы (управляется через WebSocket, не фильтруется из истории)
   * Backend отправляет список активных заказов при подключении и обновляет его через уведомления
   */
  get activeOrders(): OrderHistoryListItem[] {
    return this._activeOrders;
  }

  /**
   * Устанавливает историю заказов
   */
  setOrderHistory(orders: OrderHistoryListItem[]): void {
    runInAction(() => {
      this._orderHistory = orders;
      this.notify();
    });
  }

  /**
   * Устанавливает список активных заказов (из WebSocket)
   * Backend отправляет полный список активных заказов при подключении
   */
  setActiveOrders(orders: OrderHistoryListItem[]): void {
    runInAction(() => {
      this._activeOrders = orders;
      // Также обновляем историю
      orders.forEach(order => {
        const historyIndex = this._orderHistory.findIndex(
          o => o.order_number === order.order_number
        );
        if (historyIndex >= 0) {
          this._orderHistory[historyIndex] = order;
        } else {
          this._orderHistory.unshift(order);
        }
      });
      this.notify();
    });
  }

  /**
   * Добавляет новый заказ в историю и в активные (если он активный)
   */
  addOrder(order: OrderHistoryListItem): void {
    runInAction(() => {
      // Добавляем/обновляем в истории
      const historyIndex = this._orderHistory.findIndex(
        o => o.order_number === order.order_number
      );
      
      if (historyIndex >= 0) {
        this._orderHistory[historyIndex] = order;
      } else {
        this._orderHistory.unshift(order);
      }
      
      // Добавляем/обновляем в активных только если заказ активный
      const status = order.status as OrderStatus;
      const isActive = status !== OrderStatus.COMPLETED && 
                       status !== OrderStatus.CANCELED && 
                       status !== OrderStatus.ERROR;
      
      if (isActive) {
        const activeIndex = this._activeOrders.findIndex(
          o => o.order_number === order.order_number
        );
        
        if (activeIndex >= 0) {
          this._activeOrders[activeIndex] = order;
        } else {
          this._activeOrders.unshift(order);
        }
      } else {
        // Если заказ неактивный, удаляем из активных
        this._activeOrders = this._activeOrders.filter(
          o => o.order_number !== order.order_number
        );
      }
      
      this.notify();
    });
  }

  /**
   * Обновляет статус заказа
   * Если статус стал неактивным (COMPLETED/CANCELED/ERROR), удаляет из активных
   */
  updateOrderStatus(orderNumber: string, status: OrderStatus): void {
    runInAction(() => {
      // Обновляем в истории
      const order = this._orderHistory.find(o => o.order_number === orderNumber);
      if (order) {
        order.status = status;
      }
      
      // Проверяем активность статуса
      const isActive = status !== OrderStatus.COMPLETED && 
                       status !== OrderStatus.CANCELED && 
                       status !== OrderStatus.ERROR;
      
      if (isActive) {
        // Обновляем в активных
        const activeOrder = this._activeOrders.find(o => o.order_number === orderNumber);
        if (activeOrder) {
          activeOrder.status = status;
        } else if (order) {
          // Если заказа нет в активных, но он стал активным - добавляем
          this._activeOrders.unshift(order);
        }
      } else {
        // Удаляем из активных при неактивном статусе
        this._activeOrders = this._activeOrders.filter(
          o => o.order_number !== orderNumber
        );
      }
      
      this.notify();
    });
  }

  /**
   * Получает заказ по номеру из истории
   */
  getOrder(orderNumber: string): OrderHistoryListItem | undefined {
    return this._orderHistory.find(o => o.order_number === orderNumber);
  }
}

export const OrderStore = new OrderStoreClass();
